import { useEffect, useRef, useState } from 'react'
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { Screen } from '../components/Screen'
import { Field, useLeaveGuard } from '../components/Form'
import { EmptyState, ErrorState, SaveFailedAlert, Sheet, Skeleton } from '../components/States'
import { useEnv } from '../context'
import { useGroup, useGroupScheduleSave, useGroupScheduleTimeline } from '../data/hooks'
import { addDays, formatDayLong, formatDayShort, isoDate, parseIsoDate } from '../data/schedule'
import {
  WEEK,
  checkSlot,
  clock,
  dayName,
  dayOn,
  formatSlotTime,
  normalizeSlot,
  overlapsOf,
  scheduleKey,
  sortSlots,
} from '../data/scheduleEdit'
import {
  SliceApiError,
  type GroupSchedule,
  type ScheduleChangeItem,
  type SchedulePreview,
  type ScheduleTimeline,
  type ScheduleVersion,
} from '../data/types'
import { useBackTo } from './AthleteScreen'

/**
 * Правка расписания группы (S06, этап 3 шаги 8 и 10). Правка действует с
 * выбранной даты («это и последующие»): занятия до неё не меняются. Слоты
 * недели правятся на экране, на сервер ничего не уходит до «Сохранить
 * расписание»: сначала предпросмотр последствий для двух недель с этой даты и
 * заведённых занятий, потом явное подтверждение. Занятия с отметками никогда не
 * удаляются - они остаются в журнале и в календаре разовыми, и об этом
 * спрашивается отдельно. Дата - в адресе (`?from=`): так на правку ведёт
 * экран занятия «Изменить расписание с этого дня».
 */
export function GroupScheduleEditScreen() {
  const { id = '' } = useParams()
  const { now } = useEnv()
  const today = isoDate(now())
  const group = useGroup(id)
  const timeline = useGroupScheduleTimeline(id, today)
  const back = useBackTo(`/groups/${id}?tab=schedule`)

  if (group.isError) {
    const notFound = (group.error as { kind?: string })?.kind === 'not_found'
    return (
      <Screen title="Расписание" back={back} hideHub hideNav>
        {notFound ? (
          <EmptyState title="Группа не найдена" text="Возможно, её удалили или ссылка устарела." />
        ) : (
          <ErrorState title="Не удалось открыть расписание" onRetry={() => void group.refetch()} />
        )}
      </Screen>
    )
  }
  if (timeline.isError) {
    return (
      <Screen title="Расписание" back={back} hideHub hideNav>
        <ErrorState title="Не удалось открыть расписание" onRetry={() => void timeline.refetch()} />
      </Screen>
    )
  }
  if (group.isPending || timeline.isPending) {
    return (
      <Screen title="Расписание" back={back} hideHub hideNav>
        <Skeleton rows={3} label="Загружаю расписание" />
      </Screen>
    )
  }
  return <Editor groupId={group.data.id} groupName={group.data.name} today={today} initial={timeline.data} />
}

/** Версия, действующая в день date: текущая или последняя запланированная не позже. */
export function versionAt(tl: ScheduleTimeline, date: string): ScheduleVersion {
  let v: ScheduleVersion = tl.current
  for (const p of tl.planned) if (p.validFrom <= date) v = p
  return v
}

/** «5 октября»; сегодня - «сегодня, 5 октября». */
function dateLabel(iso: string, today?: string): string {
  const d = parseIsoDate(iso)
  const label = d ? formatDayShort(d) : iso
  return iso === today ? `сегодня, ${label}` : label
}

interface Row {
  id: string
  slot: GroupSchedule
  /** Слот из сохранённого расписания, от которого строка пошла; нет - новое занятие. */
  origin?: GroupSchedule
  /** Слот сохранённого расписания, убранный в правке: виден, пока не сохранено. */
  removed: boolean
}

let rowSeq = 0
const nextRowId = () => `row-${++rowSeq}`

/** Строки правки: draft поверх сохранённого base, совпадение по дню и началу. */
function buildRows(base: GroupSchedule[], draft: GroupSchedule[]): Row[] {
  const used = new Set<number>()
  const rows: Row[] = draft.map((slot) => {
    const i = base.findIndex(
      (b, idx) => !used.has(idx) && b.day_of_week === slot.day_of_week && clock(b.start_time) === clock(slot.start_time),
    )
    if (i >= 0) used.add(i)
    return { id: nextRowId(), slot, origin: i >= 0 ? base[i] : undefined, removed: false }
  })
  base.forEach((b, idx) => {
    if (!used.has(idx)) rows.push({ id: nextRowId(), slot: b, origin: b, removed: true })
  })
  return rows
}

function sameSlot(a: GroupSchedule, b: GroupSchedule): boolean {
  return scheduleKey([a]) === scheduleKey([b])
}

function slotLine(s: GroupSchedule): string {
  const loc = (s.location ?? '').trim()
  return `${formatSlotTime(s)}${loc ? `, ${loc}` : ''}`
}

type Phase =
  | { kind: 'edit' }
  | { kind: 'previewing' }
  | { kind: 'preview'; preview: SchedulePreview; notice?: string }
  | { kind: 'saving'; preview: SchedulePreview }
  | { kind: 'failed'; preview: SchedulePreview; error: 'network' | 'limit' | 'changed' }

function Editor({
  groupId,
  groupName,
  today,
  initial,
}: {
  groupId: string
  groupName: string
  today: string
  initial: ScheduleTimeline
}) {
  const { toast } = useEnv()
  const navigate = useNavigate()
  const location = useLocation()
  const [params, setParams] = useSearchParams()
  // Отмена и «Назад» - туда, откуда пришли (вкладка группы или занятие).
  const toGroup = useBackTo(`/groups/${groupId}?tab=schedule`)
  const timeline = useGroupScheduleTimeline(groupId, today)
  const { preview: loadPreview, save } = useGroupScheduleSave(groupId)

  /** Версии расписания, от которых идёт правка: обновляются, только если их изменили в другом месте. */
  const [tl, setTl] = useState<ScheduleTimeline>(initial)
  const maxDate = isoDate(addDays(parseIsoDate(today) ?? new Date(), 365))
  const validDate = (v: string | null): boolean => !!v && !!parseIsoDate(v) && v >= today && v <= maxDate
  // «Действует с»: из адреса (экран занятия ведёт сюда с датой), иначе - по умолчанию с сервера.
  const fromParam = params.get('from')
  const from = fromParam && validDate(fromParam) ? fromParam : validDate(tl.defaultFrom) ? tl.defaultFrom : today
  const [dateInput, setDateInput] = useState(from)
  const dateError = validDate(dateInput) ? undefined : dateInput < today ? 'Дата не раньше сегодняшнего дня' : 'Укажите дату'

  /** Версия на выбранную дату: от неё «Было», её расписание сервер ждёт как ожидаемое. */
  const baseVersion = versionAt(tl, from)
  const base = baseVersion.schedule
  const [rows, setRows] = useState<Row[]>(() => buildRows(base, base))
  const [sheet, setSheet] = useState<{ mode: 'add' } | { mode: 'edit'; rowId: string } | null>(null)
  const [phase, setPhase] = useState<Phase>({ kind: 'edit' })
  const [previewError, setPreviewError] = useState<string | null>(null)
  const [ack, setAck] = useState(false)

  const active = rows.filter((r) => !r.removed)
  const draft = active.map((r) => normalizeSlot(r.slot))
  const dirty = scheduleKey(draft) !== scheduleKey(base)
  const leave = useLeaveGuard(dirty)
  const back = () => leave.guard(toGroup)

  /** После сохранения - на вкладку «Расписание» группы, даже если пришли с занятия. */
  const done = () => {
    if ((location.state as { fromGroupTab?: boolean } | null)?.fromGroupTab) navigate(-1)
    else navigate(`/groups/${groupId}?tab=schedule`, { replace: true })
  }

  // Ошибки строк: у старых записей бывает конец раньше начала или два занятия
  // с одним началом - такое расписание сервер не примет, пока не исправить.
  const rowErrors = new Map<string, string>()
  for (const r of active) {
    const c = checkSlot(
      r.slot,
      active.filter((o) => o.id !== r.id).map((o) => o.slot),
    )
    const err = c.start ?? c.end
    if (err) rowErrors.set(r.id, err)
  }
  const overlaps = overlapsOf(draft)
  const sortedRows = sortSlots(rows.map((r) => ({ ...r.slot, _row: r }))).map((x) => x._row)

  /**
   * Сменить «Действует с». Без правок строки перестраиваются от расписания,
   * действующего в новую дату; с правками - правки остаются, а пометки
   * «Изменено», «Было» и «Новое» считаются от новой основы.
   */
  const changeFrom = (next: string, nextTl: ScheduleTimeline = tl) => {
    const nextBase = versionAt(nextTl, next).schedule
    setRows(buildRows(nextBase, dirty ? draft : nextBase))
    setDateInput(next)
    setParams(
      (p) => {
        const q = new URLSearchParams(p)
        q.set('from', next)
        return q
      },
      { replace: true, state: location.state },
    )
  }

  const runPreview = async (opts: { notice?: string; at?: string; rebased?: boolean } = {}) => {
    const at = opts.at ?? from
    setPreviewError(null)
    setAck(false)
    setPhase({ kind: 'previewing' })
    try {
      const p = await loadPreview(draft, at)
      // На сервере на эту дату уже другое расписание: правку сверяем с ним.
      if (!opts.rebased && scheduleKey(p.base.schedule) !== scheduleKey(versionAt(tl, at).schedule)) {
        await rebase(true, at, true)
        return
      }
      setPhase({ kind: 'preview', preview: p, notice: opts.notice })
    } catch (err) {
      const e = err as SliceApiError
      setPreviewError(e?.kind === 'limit' ? 'Клуб сейчас только для чтения: подписка закончилась.' : 'Не удалось проверить изменения. Правки на месте, попробуйте ещё раз.')
      setPhase({ kind: 'edit' })
    }
  }

  const confirm = async (p: SchedulePreview) => {
    setPhase({ kind: 'saving', preview: p })
    try {
      const res = await save({
        schedule: draft,
        expected: p.base.schedule,
        from: p.from,
        deleteSessionIds: p.deleteSessionIds,
        replaceVersionIds: p.replaceVersionIds,
      })
      if (!res) return // уже сохраняется
      toast(res.replayed ? 'Это расписание уже сохранено' : 'Расписание сохранено')
      done()
    } catch (err) {
      const e = err as SliceApiError
      if (e?.kind === 'conflict' && e.message === 'preview_stale') {
        // Занятия группы или запланированное расписание изменились после предпросмотра.
        await runPreview({ notice: 'Пока вы смотрели, занятия группы изменились. Список обновлён - проверьте его ещё раз.', at: p.from })
        return
      }
      setPhase({
        kind: 'failed',
        preview: p,
        error: e?.kind === 'conflict' ? 'changed' : e?.kind === 'limit' ? 'limit' : 'network',
      })
    }
  }

  /**
   * Расписание изменили в другом месте: версии перечитываются, основой
   * становится расписание на выбранную дату. С правками - сразу новый
   * предпросмотр относительно него; без них - правка начинается заново.
   */
  const rebase = async (keepDraft: boolean, at: string = from, fromPreview = false) => {
    const fresh = await timeline.refetch()
    // При сбое refetch отдаёт прежние данные: на них правку не перестраиваем.
    if (fresh.isError || !fresh.data) {
      if (fromPreview) {
        setPreviewError('Не удалось проверить изменения. Правки на месте, попробуйте ещё раз.')
        setPhase({ kind: 'edit' })
      } else {
        setPhase((ph) => (ph.kind === 'failed' ? { ...ph, error: 'network' } : ph))
      }
      return
    }
    setTl(fresh.data)
    const next = versionAt(fresh.data, at).schedule
    setRows(buildRows(next, keepDraft ? draft : next))
    if (keepDraft) {
      await runPreview({
        notice: 'Расписание группы успели изменить в другом месте. Ниже - что изменится относительно нового.',
        at,
        rebased: true,
      })
    } else setPhase({ kind: 'edit' })
  }

  const upsert = (slot: GroupSchedule, rowId?: string) => {
    setRows((rs) => {
      if (!rowId) return [...rs, { id: nextRowId(), slot, removed: false }]
      return rs.map((r) => (r.id === rowId ? { ...r, slot } : r))
    })
    setSheet(null)
  }
  const remove = (rowId: string) =>
    setRows((rs) => rs.flatMap((r) => (r.id !== rowId ? [r] : r.origin ? [{ ...r, slot: r.origin, removed: true }] : [])))
  const restore = (rowId: string) => setRows((rs) => rs.map((r) => (r.id === rowId ? { ...r, removed: false } : r)))

  const editing = sheet?.mode === 'edit' ? rows.find((r) => r.id === sheet.rowId) : undefined
  const busy = phase.kind === 'previewing' || phase.kind === 'saving'

  return (
    <Screen
      title="Расписание"
      subtitle={groupName}
      back={back}
      hideHub
      hideNav
      actionBar={
        <>
          <button type="button" className="pc-btn" style={{ flex: 1 }} onClick={back}>
            Отмена
          </button>
          <button
            type="button"
            className="pc-btn pc-btn--primary"
            style={{ flex: 2, justifyContent: 'center', textAlign: 'center' }}
            disabled={!dirty || rowErrors.size > 0 || !!dateError || busy}
            onClick={() => void runPreview()}
          >
            {phase.kind === 'previewing' ? 'Проверяю…' : 'Сохранить расписание'}
          </button>
        </>
      }
    >
      <p className="pc-muted" style={{ margin: '12px 0 0', fontSize: 14 }}>
        Занятия группы по дням недели. Правки сохранятся только после проверки, что станет с занятиями.
      </p>

      <div style={{ marginTop: 12 }}>
        <Field label="Действует с" hint="Занятия до этого дня, прошлые недели и отметки не меняются." error={dateError}>
          {(p) => (
            <input
              {...p}
              className="pc-input"
              type="date"
              min={today}
              max={maxDate}
              value={dateInput}
              disabled={busy}
              onChange={(e) => {
                const v = e.target.value
                setDateInput(v)
                if (validDate(v) && v !== from) changeFrom(v)
              }}
            />
          )}
        </Field>
      </div>
      {baseVersion.validFrom && baseVersion.validFrom > today && (
        <div className="pc-note" role="note">
          На эту дату уже запланировано расписание с {dateLabel(baseVersion.validFrom)}: правка идёт от него.
        </div>
      )}

      {previewError && (
        <div className="pc-alert" role="alert">
          {previewError}
        </div>
      )}
      {dirty && !previewError && (
        <div className="pc-note" role="status">
          Есть несохранённые изменения.
        </div>
      )}
      {rowErrors.size > 0 && (
        <div className="pc-alert" role="alert">
          Исправьте занятия с ошибкой - с ними расписание не сохранить.
        </div>
      )}
      {overlaps.length > 0 && (
        <div className="pc-aa-note" role="note" style={{ marginTop: 12 }}>
          {overlaps.map(([a, b], i) => (
            <span key={i}>
              Пересекаются по времени {dayOn(a.day_of_week)}: {formatSlotTime(a)} и {formatSlotTime(b)}. Сохранить можно, если так и задумано.
            </span>
          ))}
        </div>
      )}

      <section className="pc-section" aria-labelledby="pc-slots-title">
        <h2 id="pc-slots-title" className="pc-section-title">
          По неделям
        </h2>
        {rows.length === 0 ? (
          <EmptyState title="Занятий нет" text="Добавьте первое занятие недели." />
        ) : (
          <ul className="pc-list pc-card" style={{ padding: '0 12px' }} aria-label="Занятия недели">
            {sortedRows.map((r) => {
              const err = rowErrors.get(r.id)
              const changed = r.origin && !r.removed && !sameSlot(r.slot, r.origin)
              const label = `${dayName(r.slot.day_of_week)} ${formatSlotTime(r.slot)}`
              return (
                <li key={r.id} data-removed={r.removed || undefined}>
                  <div className="pc-slot-edit">
                    <div className="pc-slot-edit__main">
                      <div className="pc-kv__label" style={r.removed ? { textDecoration: 'line-through' } : undefined}>
                        {dayName(r.slot.day_of_week)}
                      </div>
                      <div className="pc-kv__value" style={r.removed ? { textDecoration: 'line-through' } : undefined}>
                        {slotLine(r.slot)}
                      </div>
                      <div className="pc-badges" style={{ marginTop: 6 }}>
                        {r.removed && <span className="pc-badge pc-badge--danger">Будет убрано</span>}
                        {!r.origin && <span className="pc-badge">Новое</span>}
                        {changed && <span className="pc-badge pc-badge--neutral">Изменено</span>}
                      </div>
                      {changed && r.origin && (
                        <div className="pc-muted" style={{ marginTop: 4, fontSize: 13 }}>
                          Было: {dayName(r.origin.day_of_week).toLowerCase()} {slotLine(r.origin)}
                        </div>
                      )}
                      {err && (
                        <div className="pc-field__error" style={{ marginTop: 6 }}>
                          {err}
                        </div>
                      )}
                    </div>
                    <div className="pc-slot-edit__actions">
                      {r.removed ? (
                        <button type="button" className="pc-btn" onClick={() => restore(r.id)} aria-label={`Вернуть: ${label}`}>
                          Вернуть
                        </button>
                      ) : (
                        <>
                          <button
                            type="button"
                            className="pc-btn"
                            onClick={() => setSheet({ mode: 'edit', rowId: r.id })}
                            aria-label={`Изменить: ${label}`}
                          >
                            Изменить
                          </button>
                          <button type="button" className="pc-btn" onClick={() => remove(r.id)} aria-label={`Удалить: ${label}`}>
                            Удалить
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                </li>
              )
            })}
          </ul>
        )}
        <button type="button" className="pc-btn pc-btn--block" style={{ marginTop: 12, justifyContent: 'center' }} onClick={() => setSheet({ mode: 'add' })}>
          Добавить занятие
        </button>
      </section>

      {sheet && (
        <SlotSheet
          initial={editing?.slot}
          others={active.filter((r) => r.id !== editing?.id).map((r) => r.slot)}
          onCancel={() => setSheet(null)}
          onDone={(slot) => upsert(slot, editing?.id)}
        />
      )}

      {(phase.kind === 'preview' || phase.kind === 'saving' || phase.kind === 'failed') && (
        <PreviewSheet
          phase={phase}
          ack={ack}
          onAck={setAck}
          today={today}
          onConfirm={() => void confirm(phase.preview)}
          onClose={() => setPhase({ kind: 'edit' })}
          onRebase={(keep) => void rebase(keep, phase.preview.from)}
          onStartFrom={(date) => {
            changeFrom(date)
            void runPreview({ at: date })
          }}
        />
      )}
      {leave.dialog}
    </Screen>
  )
}

// ---------------------------------------------------------------------------
// Шторка занятия: день, начало, конец, зал
// ---------------------------------------------------------------------------

function SlotSheet({
  initial,
  others,
  onCancel,
  onDone,
}: {
  initial?: GroupSchedule
  others: GroupSchedule[]
  onCancel: () => void
  onDone: (slot: GroupSchedule) => void
}) {
  const [day, setDay] = useState(initial?.day_of_week ?? 1)
  const [start, setStart] = useState(initial ? clock(initial.start_time) : '18:00')
  const [end, setEnd] = useState(initial ? clock(initial.end_time) : '19:30')
  const [location, setLocation] = useState(initial?.location ?? '')

  const slot: GroupSchedule = normalizeSlot({ day_of_week: day, start_time: start, end_time: end, location })
  const check = checkSlot(slot, others)
  const invalid = !!(check.start || check.end)

  return (
    <Sheet title={initial ? 'Изменить занятие' : 'Новое занятие'} onClose={onCancel}>
      <form
        className="pc-form"
        noValidate
        onSubmit={(e) => {
          e.preventDefault()
          if (!invalid) onDone(slot)
        }}
      >
        <div className="pc-field">
          <span className="pc-field__label" id="pc-slot-day">
            День недели
          </span>
          <div className="pc-chips pc-chips--touch" role="group" aria-labelledby="pc-slot-day">
            {WEEK.map((w) => (
              <button key={w.day} type="button" aria-pressed={day === w.day} aria-label={w.long} onClick={() => setDay(w.day)}>
                {w.short}
              </button>
            ))}
          </div>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, alignItems: 'start' }}>
          <Field label="Начало" error={check.start}>
            {(p) => <input {...p} className="pc-input" type="time" value={start} onChange={(e) => setStart(e.target.value)} />}
          </Field>
          <Field label="Конец" error={check.end}>
            {(p) => <input {...p} className="pc-input" type="time" value={end} onChange={(e) => setEnd(e.target.value)} />}
          </Field>
        </div>
        <Field label="Зал или место" hint="Можно не указывать">
          {(p) => <input {...p} className="pc-input" value={location} maxLength={200} onChange={(e) => setLocation(e.target.value)} />}
        </Field>
        {check.overlaps.length > 0 && (
          <div className="pc-aa-note" role="note">
            Пересекается {dayOn(day)} с {check.overlaps.map(formatSlotTime).join(', ')}. Сохранить можно, если так и задумано.
          </div>
        )}
        <button
          type="submit"
          className="pc-btn pc-btn--primary pc-btn--block"
          style={{ justifyContent: 'center', marginTop: 12 }}
          disabled={invalid}
        >
          Готово
        </button>
        <button type="button" className="pc-btn pc-btn--block" style={{ justifyContent: 'center' }} onClick={onCancel}>
          Отмена
        </button>
        <p className="pc-muted" style={{ margin: '8px 0 0', fontSize: 13 }}>
          Занятие попадёт в правку, сохранится вместе с расписанием.
        </p>
      </form>
    </Sheet>
  )
}

// ---------------------------------------------------------------------------
// Предпросмотр последствий и подтверждение
// ---------------------------------------------------------------------------

function dayLabel(iso: string): string {
  const d = parseIsoDate(iso)
  return d ? formatDayLong(d) : iso
}

function range(start?: string, end?: string): string {
  return end ? `${start} - ${end}` : start ?? ''
}

/** Что произойдёт с занятием - одной-двумя строками, без рода. */
function describe(it: ScheduleChangeItem): { what: string; detail?: string; danger?: boolean } {
  const oldT = range(it.oldStart, it.oldEnd)
  const newT = range(it.newStart, it.newEnd)
  let what: string
  switch (it.change) {
    case 'added':
      what = `Новое занятие ${newT}${it.newLocation ? `, ${it.newLocation}` : ''}`
      break
    case 'removed':
      what = it.outcome === 'kept' ? `Занятие ${oldT} выпадает из расписания` : `Занятия ${oldT} не будет`
      break
    case 'moved':
      what = `${oldT} → ${newT}`
      break
    default: {
      const parts: string[] = []
      if (it.oldEnd !== it.newEnd) parts.push(`конец ${it.newEnd} вместо ${it.oldEnd}`)
      if ((it.oldLocation ?? '') !== (it.newLocation ?? '')) parts.push(it.newLocation ? `место: ${it.newLocation}` : 'место не указано')
      what = `${it.oldStart}: ${parts.join(', ')}`
    }
  }
  let detail: string | undefined
  let danger = false
  if (it.outcome === 'kept' && it.session) {
    detail =
      it.change === 'moved'
        ? `Отметок на занятии ${it.oldStart}: ${it.session.marks}. Оно останется разовым рядом с новым, отметки и журнал не изменятся.`
        : `Отметок на занятии: ${it.session.marks}. Оно останется разовым, отметки и журнал не изменятся.`
    danger = true
  } else if (it.outcome === 'deleted' && it.session?.cancelled) {
    detail = `Это занятие было отменено - отмена уберётся вместе с ним.`
  } else if (it.outcome === 'deleted') {
    detail = `Заведённое занятие без отметок уберётся.`
  }
  if (it.newCancelled) {
    detail = [detail, `В ${it.newStart} в этот день уже есть отменённое занятие - оно так и останется отменённым.`].filter(Boolean).join(' ')
  }
  return { what, detail, danger }
}

function ChangeList({ items, label }: { items: ScheduleChangeItem[]; label: string }) {
  return (
    <ul className="pc-list pc-changes" aria-label={label}>
      {items.map((it, i) => {
        const d = describe(it)
        return (
          <li key={`${it.date}-${it.oldStart ?? ''}-${it.newStart ?? ''}-${i}`} className="pc-change">
            <span className="pc-change__who">{dayLabel(it.date)}</span>
            <span className="pc-change__what">{d.what}</span>
            {d.detail && (
              <span className="pc-change__what" style={d.danger ? { color: 'var(--pc-danger)', fontWeight: 600 } : undefined}>
                {d.detail}
              </span>
            )}
          </li>
        )
      })}
    </ul>
  )
}

function VersionSlots({ slots, label }: { slots: GroupSchedule[]; label: string }) {
  if (slots.length === 0) {
    return (
      <p className="pc-muted" style={{ margin: '4px 0 0', fontSize: 14 }}>
        Занятий по расписанию не было.
      </p>
    )
  }
  return (
    <ul className="pc-list pc-changes" aria-label={label}>
      {sortSlots(slots).map((s, i) => (
        <li key={i} className="pc-change">
          <span className="pc-change__who">{dayName(s.day_of_week)}</span>
          <span className="pc-change__what">{slotLine(s)}</span>
        </li>
      ))}
    </ul>
  )
}

function PreviewSheet({
  phase,
  ack,
  today,
  onAck,
  onConfirm,
  onClose,
  onRebase,
  onStartFrom,
}: {
  phase: Extract<Phase, { preview: SchedulePreview }>
  ack: boolean
  today: string
  onAck: (v: boolean) => void
  onConfirm: () => void
  onClose: () => void
  onRebase: (keepDraft: boolean) => void
  onStartFrom: (date: string) => void
}) {
  const p = phase.preview
  // Шторка ставит фокус на первую кнопку - здесь это «Сохранить» внизу
  // списка: шторка открылась бы прокрученной к подтверждению. Начинаем с
  // начала списка, фокус - на заголовок.
  const top = useRef<HTMLParagraphElement>(null)
  useEffect(() => {
    top.current?.closest('.pc-sheet')?.scrollTo?.(0, 0)
    top.current?.focus({ preventScroll: true })
  }, [p])
  const near = p.items.filter((i) => !i.later)
  const later = p.items.filter((i) => i.later)
  const to = parseIsoDate(p.to)
  const needAck = p.keptMarked > 0
  const saving = phase.kind === 'saving'

  if (phase.kind === 'failed' && phase.error === 'changed') {
    return (
      <Sheet title="Расписание уже изменили" onClose={onClose}>
        <p style={{ margin: '0 0 12px', fontSize: 15 }}>
          Пока вы правили, расписание группы изменили в другом месте (другое окно, бот или другой тренер). Ничего не сохранено.
        </p>
        <button type="button" className="pc-btn pc-btn--primary pc-btn--block" style={{ justifyContent: 'center' }} onClick={() => onRebase(true)}>
          Сравнить мои правки с новым расписанием
        </button>
        <button type="button" className="pc-btn pc-btn--block" style={{ justifyContent: 'center' }} onClick={() => onRebase(false)}>
          Отказаться от правок и открыть новое
        </button>
      </Sheet>
    )
  }

  return (
    <Sheet title="Проверьте изменения" onClose={saving ? () => {} : onClose}>
      {'notice' in phase && phase.notice && (
        <div className="pc-note" role="status" style={{ marginTop: 4 }}>
          {phase.notice}
        </div>
      )}
      <p ref={top} tabIndex={-1} style={{ margin: '8px 0 0', fontSize: 15, outline: 'none' }}>
        Новое расписание действует с {dateLabel(p.from, today)}. Занятия до этого дня, прошлые недели, журнал и отметки не меняются.
      </p>

      {p.replaced.length > 0 && (
        <section aria-label="Заменяется запланированное расписание">
          {p.replaced.map((v) => (
            <div key={v.id} className="pc-aa-note" style={{ marginTop: 12 }}>
              <span style={{ fontWeight: 600 }}>Запланированное с {dateLabel(v.validFrom)} расписание будет заменено этим.</span>
              <VersionSlots slots={v.schedule} label={`Запланированное с ${dateLabel(v.validFrom)}`} />
            </div>
          ))}
        </section>
      )}

      <h3 className="pc-section-title" style={{ marginTop: 16, fontSize: 16 }}>
        Две недели: {dateLabel(p.from)} - {to ? formatDayShort(to) : p.to}
      </h3>
      {near.length === 0 ? (
        <p className="pc-muted" style={{ margin: '4px 0 0', fontSize: 14 }}>
          {p.changed ? 'В эти две недели занятия не меняются.' : 'С этой даты расписание и так такое.'}
        </p>
      ) : (
        <ChangeList items={near} label="Две недели с начала действия" />
      )}

      {later.length > 0 && (
        <>
          <h3 className="pc-section-title" style={{ marginTop: 16, fontSize: 16 }}>
            Позже
          </h3>
          <ChangeList items={later} label="Позже" />
        </>
      )}

      {p.suggestedFrom && (
        <div className="pc-aa-note" role="note" style={{ marginTop: 12 }}>
          <span>
            Занятия с отметками останутся разовыми рядом с новыми. Чтобы этого не было, начните новое расписание с{' '}
            {dateLabel(p.suggestedFrom)}.
          </span>
          <button
            type="button"
            className="pc-btn pc-btn--block"
            style={{ justifyContent: 'center', marginTop: 8 }}
            disabled={saving}
            onClick={() => onStartFrom(p.suggestedFrom!)}
          >
            Начать с {dateLabel(p.suggestedFrom)}
          </button>
        </div>
      )}

      {needAck && (
        <label className="pc-ack">
          <input type="checkbox" checked={ack} onChange={(e) => onAck(e.target.checked)} />
          <span>
            Понятно: занятия с отметками выпадут из расписания и останутся разовыми, отметки сохранятся. Таких занятий:{' '}
            {p.keptMarked}.
          </span>
        </label>
      )}

      {phase.kind === 'failed' &&
        (phase.error === 'limit' ? (
          <div className="pc-alert" role="alert">
            Клуб сейчас только для чтения: подписка закончилась. Расписание не сохранено.
          </div>
        ) : (
          <SaveFailedAlert>Правки на месте, можно повторить.</SaveFailedAlert>
        ))}

      <button
        type="button"
        className="pc-btn pc-btn--primary pc-btn--block"
        style={{ justifyContent: 'center', marginTop: 16 }}
        disabled={saving || (needAck && !ack) || !p.changed || (phase.kind === 'failed' && phase.error === 'limit')}
        onClick={onConfirm}
      >
        {saving ? 'Сохраняю…' : phase.kind === 'failed' ? 'Повторить сохранение' : 'Сохранить расписание'}
      </button>
      <button type="button" className="pc-btn pc-btn--block" style={{ justifyContent: 'center' }} disabled={saving} onClick={onClose}>
        Вернуться к правке
      </button>
    </Sheet>
  )
}
